import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { format, differenceInCalendarDays, startOfDay } from "date-fns";

/** INR formatter — mirrors account-detail.tsx's formatINR (no shared @/lib/format money fn exists yet). */
function formatINR(amount: number | null | undefined): string {
  const n = Number(amount ?? 0);
  if (!Number.isFinite(n)) return "—";
  return `₹${new Intl.NumberFormat("en-IN", {
    maximumFractionDigits: 0,
  }).format(n)}`;
}

function fmtDate(d: string | Date | null | undefined): string {
  if (!d) return "—";
  return format(new Date(d), "d MMM yyyy");
}

const CYCLE_SUFFIX: Record<string, string> = {
  Monthly: "/mo",
  Quarterly: "/qtr",
  Six_Month: "/6mo",
  Yearly: "/yr",
};

function cycleSuffix(cycleMode: string | null | undefined): string {
  if (!cycleMode) return "";
  return CYCLE_SUFFIX[cycleMode] ?? "";
}

function isOneTime(cycleMode: string | null | undefined): boolean {
  return cycleMode === "One_Time";
}

/**
 * Day X of N / days-left, computed purely from period start/end vs "today".
 * Inclusive day counting (a 1-day period is "Day 1 of 1"); clamps X to
 * [0, N] so a not-yet-started or already-ended period never renders a
 * negative/over-100% bar.
 */
function computeProgress(
  start: string | Date | null | undefined,
  end: string | Date | null | undefined,
): { dayX: number; totalDays: number; daysLeft: number; pct: number } | null {
  if (!start || !end) return null;
  const s = startOfDay(new Date(start));
  const e = startOfDay(new Date(end));
  const totalDays = differenceInCalendarDays(e, s) + 1;
  if (!Number.isFinite(totalDays) || totalDays <= 0) return null;
  const today = startOfDay(new Date());
  const rawElapsed = differenceInCalendarDays(today, s) + 1;
  const dayX = Math.min(Math.max(rawElapsed, 0), totalDays);
  const daysLeft = Math.max(totalDays - dayX, 0);
  const pct = Math.round((dayX / totalDays) * 100);
  return { dayX, totalDays, daysLeft, pct };
}

/** Data shape the assembly plan (38-09) passes in — the grouped running-plan
 * deal (quoteGroupId → "Plan #{firstLine.id}"), NOT a raw Membership. */
export interface RunningPlanProgressPlan {
  /** first-line membership id — drives the uniform "Plan #{id}" label. */
  id: number;
  /** display plan name, e.g. "Workstation" (already resource-type formatted). */
  name: string;
  /** billing cadence: Monthly | Quarterly | Six_Month | Yearly | One_Time | other. */
  cycleMode?: string | null;
  periodStart?: string | Date | null;
  periodEnd?: string | Date | null;
  /** rupee amount due per cycle (or for the whole term when One_Time). */
  price: number;
  autoRenewDate?: string | Date | null;
}

/**
 * Member profile enrichment (Phase 38) — running-plan card: Recurring/
 * One-time badge, "Plan #{id}", period range, ₹/cycle, "Auto-renews {date}",
 * and a Day X of N · N days left progress bar computed purely from
 * period start/end vs today. Pure props-driven presentation — no data
 * fetching here; the assembly plan (38-09) supplies the grouped plan.
 * Standalone — not wired into member-profile.tsx by this plan.
 */
export function RunningPlanProgress({
  plan,
}: {
  plan: RunningPlanProgressPlan | null | undefined;
}) {
  if (!plan) {
    return (
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-semibold">Running plan</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">No running plan</p>
        </CardContent>
      </Card>
    );
  }

  const oneTime = isOneTime(plan.cycleMode);
  const progress = computeProgress(plan.periodStart, plan.periodEnd);

  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-sm font-semibold">
            Plan #{plan.id}
          </CardTitle>
          <Badge variant={oneTime ? "secondary" : "default"}>
            {oneTime ? "One-time" : "Recurring"}
          </Badge>
        </div>
        <p className="text-sm font-medium">{plan.name}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
          <p className="text-muted-foreground">
            {fmtDate(plan.periodStart)} – {fmtDate(plan.periodEnd)}
          </p>
          <p className="font-semibold tabular-nums">
            {formatINR(plan.price)}
            {cycleSuffix(plan.cycleMode)}
          </p>
        </div>

        {plan.autoRenewDate ? (
          <p className="text-xs text-muted-foreground">
            Auto-renews {fmtDate(plan.autoRenewDate)}
          </p>
        ) : oneTime ? (
          <p className="text-xs text-muted-foreground">
            One-time — ends {fmtDate(plan.periodEnd)}
          </p>
        ) : null}

        {progress ? (
          <div className="space-y-1">
            <Progress value={progress.pct} />
            <p className="text-xs text-muted-foreground">
              Day {progress.dayX} of {progress.totalDays} · {progress.daysLeft}{" "}
              days left
            </p>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
